import { installLensHost } from "./installLensHost.js";
import { CLOSE_DISPLACED, FATAL_CLOSE_CODES, ServerMessageSchema, } from "@lenscore-pipeline/host-protocol";
import { publishLensHostStatus, publishLensHostFatal } from "./lensHostStatusBus.js";
export function installLensHostWebSocket(options) {
    if (typeof window === "undefined")
        return;
    const { url, role, id = `${role}-${randomHex(8)}`, reconnect = true, reconnectInitialMs = 500, reconnectMaxMs = 30_000, onPresence, onSnapshot, onStatus, onError, } = options;
    if (typeof url !== "string" || url.length === 0) {
        throw new TypeError("installLensHostWebSocket: url is required");
    }
    if (role !== "renderer" && role !== "control") {
        throw new TypeError("installLensHostWebSocket: role must be 'renderer' or 'control'");
    }
    /** @type {WebSocket | null} */
    let socket = null;
    let backoff = reconnectInitialMs;
    let disposed = false;
    /** @type {string | null} */
    let lastFatalReason = null;
    function setStatus(s) {
        publishLensHostStatus(s);
        try {
            onStatus?.(s);
        }
        catch { /* swallow consumer errors */ }
    }
    function setFatal(reason) {
        lastFatalReason = reason;
        publishLensHostFatal(reason);
    }
    function emitError(err) {
        try {
            onError?.(err);
        }
        catch { /* swallow */ }
    }
    function sendNow(message) {
        if (!socket || socket.readyState !== WebSocket.OPEN)
            return;
        try {
            socket.send(JSON.stringify(message));
        }
        catch (err) {
            emitError(err);
        }
    }
    installLensHost({
        sendToHost: (message) => sendNow(message),
    });
    function connect() {
        if (disposed || lastFatalReason !== null)
            return;
        setStatus("connecting");
        let ws;
        try {
            ws = new WebSocket(url);
        }
        catch (err) {
            emitError(err);
            scheduleReconnect();
            return;
        }
        socket = ws;
        ws.addEventListener("open", () => {
            backoff = reconnectInitialMs;
            setStatus("open");
            sendNow({ type: "hello", role, id });
        });
        ws.addEventListener("message", (event) => {
            const parsed = ServerMessageSchema.safeParse(safeJsonParse(event.data));
            if (!parsed.success) {
                emitError(new Error("Invalid inbound WebSocket message"));
                return;
            }
            handleServerMessage(parsed.data);
        });
        ws.addEventListener("close", (event) => {
            socket = null;
            const code = /** @type {CloseEvent} */ (event).code;
            const reason = /** @type {CloseEvent} */ (event).reason || "";
            if (FATAL_CLOSE_CODES.has(code)) {
                setFatal(formatFatalReason(code, reason));
                setStatus("closed");
                return; // do not reconnect
            }
            setStatus("closed");
            if (reconnect)
                scheduleReconnect();
        });
        ws.addEventListener("error", (event) => {
            emitError(event);
            // The browser fires `close` shortly after `error`; reconnect happens there.
        });
    }
    function handleServerMessage(msg) {
        switch (msg.type) {
            case "command":
                window.lensCommand?.(msg.command, msg.payload, msg.requestId);
                return;
            case "presence":
                try {
                    onPresence?.(msg.clients);
                }
                catch { /* swallow */ }
                return;
            case "snapshot":
                try {
                    onSnapshot?.(msg.renderers);
                }
                catch { /* swallow */ }
                return;
            case "commandResult":
            case "state":
            case "ready":
            case "log":
                // Peer messages — a renderer doesn't need to act on another
                // renderer's state. Controls that want to surface peer state
                // should add their own listener.
                return;
        }
    }
    function scheduleReconnect() {
        if (disposed || lastFatalReason !== null)
            return;
        const delay = backoff;
        backoff = Math.min(backoff * 2, reconnectMaxMs);
        setTimeout(connect, delay);
    }
    connect();
}
/**
 * @param {number} code
 * @param {string} reason
 * @returns {string}
 */
function formatFatalReason(code, reason) {
    if (code === CLOSE_DISPLACED) {
        return reason || "Another client joined with the same id and replaced this connection.";
    }
    if (code === 1003)
        return reason || "Disconnected: invalid message format.";
    if (code === 1008)
        return reason || "Disconnected: policy violation.";
    return reason || `Disconnected (code ${code}).`;
}
/**
 * @param {unknown} raw
 * @returns {unknown}
 */
function safeJsonParse(raw) {
    if (typeof raw !== "string")
        return raw;
    try {
        return JSON.parse(raw);
    }
    catch {
        return null;
    }
}
/**
 * @param {number} bytes
 * @returns {string}
 */
function randomHex(bytes) {
    const arr = new Uint8Array(bytes);
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
        crypto.getRandomValues(arr);
    }
    else {
        for (let i = 0; i < bytes; i++)
            arr[i] = Math.floor(Math.random() * 256);
    }
    return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}
//# sourceMappingURL=installLensHostWebSocket.js.map